import React from "react";
import { Table } from "react-bootstrap";
import { useLingui } from "@lingui/react/macro";

import { createAkaUrl } from "@/lib/aka";
import {
    formatDiffPct,
    formatSlope,
    getDiffVariant,
    isModelsStagingRowSelectable,
    type ModelsStagingSortDirection,
    type ModelsStagingSortField,
    type ModelsStagingViewRow,
} from "./modelsStaging.lib";
import { classNames } from "@/lib/util";
import SimpleLink from "@/modules/shared/ui/SimpleLink";

type Props = {
    rows: ModelsStagingViewRow[];
    sortField: ModelsStagingSortField;
    sortDirection: ModelsStagingSortDirection;
    allSelected: boolean;
    hasSelectableRows: boolean;
    onSort: (field: ModelsStagingSortField) => void;
    onToggleAll: () => void;
    onToggleRow: (modelCode: string) => void;
};

const sortIndicator = (
    field: ModelsStagingSortField,
    sortField: ModelsStagingSortField,
    sortDirection: ModelsStagingSortDirection
) => {
    if (field !== sortField) {
        return "";
    }
    return sortDirection === "asc" ? " ▲" : " ▼";
};

const ModelsStagingTable = ({
    rows,
    sortField,
    sortDirection,
    allSelected,
    hasSelectableRows,
    onSort,
    onToggleAll,
    onToggleRow,
}: Props) => {
    const { t } = useLingui();

    const renderSortableHeader = (field: ModelsStagingSortField, label: string, className?: string) => (
        <th className={className}>
            <button type="button" className="btn btn-link p-0 text-decoration-none" onClick={() => onSort(field)}>
                {label}
                {sortIndicator(field, sortField, sortDirection)}
            </button>
        </th>
    );

    const renderDiffCell = (value: number | null) => (
        <td className="text-end">
            <span className={classNames("badge delta-badge", getDiffVariant(value))}>{formatDiffPct(value)}</span>
        </td>
    );

    return (
        <div className="table-responsive">
            <Table size="sm" responsive className="dashboard-table models-staging-table mb-0">
                <thead className="table-header-neutral">
                    <tr>
                        <th>
                            <input
                                type="checkbox"
                                checked={allSelected}
                                disabled={!hasSelectableRows}
                                onChange={onToggleAll}
                            />
                        </th>
                        {renderSortableHeader("plant_title", t`Plant`)}
                        {renderSortableHeader("line_title", t`Line`)}
                        {renderSortableHeader("material_title", t`Material`)}
                        {renderSortableHeader("process_num", t`Process`)}
                        {renderSortableHeader("active_slope", t`Active slope (Old)`, "text-end")}
                        {renderSortableHeader("pending_slope", t`Pending slope (New)`, "text-end")}
                        {renderSortableHeader("norm_slope", t`Norm slope`, "text-end")}
                        {renderSortableHeader("old_vs_new_diff_pct", t`Δ Old vs New %`, "text-end")}
                        {renderSortableHeader("old_vs_norm_diff_pct", t`Δ Old vs Norm %`, "text-end")}
                        {renderSortableHeader("new_vs_norm_diff_pct", t`Δ New vs Norm %`, "text-end")}
                        <th>&nbsp;</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(row => (
                        <tr key={row.model_code}>
                            <td>
                                {isModelsStagingRowSelectable(row) ? (
                                    <input
                                        type="checkbox"
                                        checked={row.is_selected}
                                        onChange={() => onToggleRow(row.model_code)}
                                    />
                                ) : null}
                            </td>
                            <td>
                                <strong>{row.plant_title}</strong>
                            </td>
                            <td>
                                <SimpleLink
                                    text={row.line_external_id}
                                    url={createAkaUrl({
                                        type: "man_line",
                                        external_id: row.line_external_id,
                                    })}
                                    tooltip={row.line_title}
                                    is_active={true}
                                />
                            </td>
                            <td>
                                <SimpleLink
                                    text={row.material_external_id}
                                    url={createAkaUrl({
                                        type: "man_material",
                                        external_id: row.material_external_id,
                                    })}
                                    tooltip={row.material_title}
                                    is_active={true}
                                />
                            </td>
                            <td>{row.process_num}</td>
                            <td className="text-end">{formatSlope(row.active_slope)}</td>
                            <td className="text-end">{formatSlope(row.pending_slope)}</td>
                            <td className="text-end">{formatSlope(row.norm_slope)}</td>
                            {renderDiffCell(row.old_vs_new_diff_pct)}
                            {renderDiffCell(row.old_vs_norm_diff_pct)}
                            {renderDiffCell(row.new_vs_norm_diff_pct)}
                            <td>
                                <SimpleLink
                                    text={t`Details`}
                                    url={createAkaUrl({
                                        type: "man_model",
                                        line: row.line_external_id,
                                        material: row.material_external_id,
                                        process_num: row.process_num,
                                    })}
                                    tooltip={t`Show model details`}
                                    is_active={true}
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </div>
    );
};

export default ModelsStagingTable;
